import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Slider,
  TextField,
  IconButton
} from '@mui/material';
import Phonelink from '@mui/icons-material/Phonelink';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { getMinUsersForTier } from '../../../../utils/tierUsers';
import { useCalculator } from '../../../../contexts/CalculatorContext';
import TierSelector from './TierSelector';
import TierCompareDialog from './TierCompareDialog';

// Features included in each tier
const TIER_FEATURES = {
  basic: [
    { key: 'mobileDesktopApps', default: 'Desktop & mobile apps' },
    { key: 'callRecordingUnlimited', default: 'Unlimited call recording' },
    { key: 'integrations50', default: '50+ ready integrations' },
    { key: 'reportsControlMissed', default: 'Reports & statistics' },
    { key: 'flexibleRouting', default: 'Flexible call routing' },
    { key: 'ivrVoicemail', default: 'IVR, voicemail' },
  ],
  pro: [
    { key: 'allBasicFeatures', default: 'All Basic plan features' },
    { key: 'callTranscription', default: 'Call transcription' },
    { key: 'aiQualityScoring', default: 'AI call quality scoring' },
    { key: 'omnichannelMessengers', default: 'Chat & messengers' },
    { key: 'callbackWidget', default: 'Callback widget' },
    { key: 'advancedReportsSchedule', default: 'Advanced reporting' },
  ],
  advanced: [
    { key: 'allProFeatures', default: 'All Pro plan features' },
    { key: 'powerDialer', default: 'Power Dialer' },
    { key: 'missedCallsAutomation', default: 'Missed calls automation' },
    { key: 'prioritySupport', default: 'Priority support' },
    { key: 'customReportsLooker', default: 'Custom Looker reports' },
    { key: 'aiChatAddons', default: 'AI & Chat as add-ons' },
  ],
  corporate: [
    { key: 'allProFeatures', default: 'All Pro plan features' },
    { key: 'advancedConversationAnalytics', default: 'Advanced conversation analytics' },
    { key: 'powerDialer', default: 'Power Dialer' },
    { key: 'missedCallsAutomation', default: 'Missed calls automation' },
    { key: 'personalManager', default: 'Dedicated Customer Success' },
    { key: 'prioritySupport', default: 'Priority support' },
    { key: 'customReportsCrm', default: 'Custom reports & integrations' },
  ],
};

/**
 * BusinessPhone component for the pricing calculator
 * @param {Object} props - Component props
 * @param {Object} props.product - Product data (value, min, max, etc.)
 * @param {Function} props.toggleProduct - Function to toggle product selection
 */
const BusinessPhone = ({ product, toggleProduct }) => {
  const { t } = useTranslation();
  const {
    setBusinessPhoneSettingsOpen,
    businessPhoneType,
    fmcUsers,
    setFmcUsers,
    hasBusinessPhoneCustomSettings,
    region,
    veryCustomProductDiscounts,
    packageTier,
    setPackageTier,
    showAdvancedTier,
    showPricesWithoutBonuses
  } = useCalculator();

  // Compare dialog state
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);

  // Get minimum users based on tier
  const minUsers = getMinUsersForTier(packageTier);

  // Local state for inputs
  const [businessPhoneInput, setBusinessPhoneInput] = useState(product.value);
  const [fmcInput, setFmcInput] = useState(fmcUsers);

  // Sync local state if the prop values change
  useEffect(() => {
    if (product.value !== businessPhoneInput) {
      setBusinessPhoneInput(product.value);
    }
  }, [product.value]);

  useEffect(() => {
    if (fmcUsers !== fmcInput) {
      setFmcInput(fmcUsers);
    }
  }, [fmcUsers]);

  // Auto-adjust users when switching to a tier with a higher minimum
  useEffect(() => {
    if (product.value < minUsers) {
      product.setValue(minUsers);
      setBusinessPhoneInput(minUsers);
    }
  }, [packageTier]);

  // Check if region is Ukraine
  const isUkraineRegion = region === 'ua' ||
    String(region).toLowerCase().includes('ukr') ||
    String(region).toLowerCase().includes('ua') ||
    String(region).toLowerCase() === 'ukraine';

  // Open business phone settings dialog
  const handleOpenSettings = () => {
    setBusinessPhoneSettingsOpen(true);
  };

  // Check if there are any custom settings
  const hasAnyCustomSettings = hasBusinessPhoneCustomSettings ||
    (veryCustomProductDiscounts['business_phone'] && veryCustomProductDiscounts['business_phone'] > 0);

  // Get tier display name for header
  const getTierDisplayName = () => {
    const tierNames = {
      basic: 'Basic',
      pro: 'Pro',
      advanced: 'Advanced',
      corporate: 'Corporate'
    };
    return tierNames[packageTier] || 'Basic';
  };

  // Get features for current tier
  const currentFeatures = TIER_FEATURES[packageTier] || TIER_FEATURES.basic;

  // Get product name for header
  const getProductName = () => {
    if (businessPhoneType === 'fmc') return t('calculator.fmcOnly', 'FMC');
    if (businessPhoneType === 'business-phone-fmc') return t('calculator.businessPhoneFmc', 'Business Phone + FMC');
    return 'Business Phone';
  };

  return (
    <>
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, mx: { md: -4 }, my: { md: -4 } }}>
      {/* Left part: 70% */}
      <Box sx={{
        width: { xs: '100%', md: '70%' },
        p: { xs: 2, md: 4 },
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Phonelink sx={{ color: '#333', fontSize: 20, mr: 1 }} />
            <Typography sx={{ fontWeight: 500 }}>
              {getProductName()} {getTierDisplayName()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={handleOpenSettings}
              sx={{
                color: hasAnyCustomSettings ? '#00C853' : '#757575',
                mr: 1,
                '&:hover': { color: '#00C853' }
              }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => toggleProduct(product.id)}
              sx={{ color: '#333', '&:hover': { color: '#00C853' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Tier Selector */}
        <TierSelector
          selected={packageTier}
          onChange={setPackageTier}
          region={region}
          onCompareClick={() => setCompareDialogOpen(true)}
          showAdvancedTier={showAdvancedTier}
        />

        {/* Slider section - pushed to bottom */}
        <Box sx={{ mt: 'auto' }}>
          {/* Business Phone Slider - Hide if FMC only */}
          {businessPhoneType !== 'fmc' && (
            <Box sx={{ mb: businessPhoneType === 'business-phone-fmc' && isUkraineRegion ? 2 : 0 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                {businessPhoneType === 'business-phone-fmc'
                  ? t('calculator.businessPhoneUsersLabel', 'Number of business phone users')
                  : t('calculator.businessPhoneUsersLabelStandalone', 'Number of users')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Slider
                    value={product.value}
                    min={minUsers}
                    max={100}
                    onChange={(_, v) => {
                      product.setValue(Number(v));
                      setBusinessPhoneInput(Number(v));
                    }}
                    sx={{
                      color: '#00C853',
                      '& .MuiSlider-thumb': {
                        width: 16,
                        height: 16,
                        backgroundColor: '#00C853',
                      }
                    }}
                  />
                </Box>
                <TextField
                  value={businessPhoneInput}
                  onChange={e => setBusinessPhoneInput(e.target.value)}
                  onBlur={() => {
                    let numericValue = parseInt(businessPhoneInput, 10);
                    if (isNaN(numericValue) || numericValue < minUsers) {
                      numericValue = minUsers;
                    }
                    setBusinessPhoneInput(numericValue);
                    product.setValue(numericValue);
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  sx={{ width: 90 }}
                  inputProps={{ inputMode: 'numeric' }}
                />
              </Box>
            </Box>
          )}

          {/* FMC Slider - Show only if FMC or Business Phone + FMC and region is Ukraine */}
          {(businessPhoneType === 'fmc' || businessPhoneType === 'business-phone-fmc') && isUkraineRegion && (
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                {businessPhoneType === 'business-phone-fmc'
                  ? t('calculator.fmcUsersLabel', 'Number of FMC users')
                  : t('calculator.fmcUsersLabelStandalone', 'Number of users')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Slider
                    value={fmcUsers}
                    min={businessPhoneType === 'fmc' ? 6 : 0}
                    max={100}
                    onChange={(_, v) => {
                      setFmcUsers(Number(v));
                      setFmcInput(Number(v));
                    }}
                    sx={{
                      color: '#00C853',
                      '& .MuiSlider-thumb': {
                        width: 16,
                        height: 16,
                        backgroundColor: '#00C853',
                      }
                    }}
                  />
                </Box>
                <TextField
                  value={fmcInput}
                  onChange={e => setFmcInput(e.target.value)}
                  onBlur={() => {
                    let numericValue = parseInt(fmcInput, 10);
                    if (isNaN(numericValue) || numericValue < 0) {
                      numericValue = 0;
                    }
                    if (businessPhoneType === 'fmc' && numericValue < 6) {
                      numericValue = 6;
                    }
                    setFmcInput(numericValue);
                    setFmcUsers(numericValue);
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  sx={{ width: 90 }}
                  inputProps={{ inputMode: 'numeric' }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right part: 30% - Features */}
      <Box sx={{
        width: { xs: '100%', md: '30%' },
        bgcolor: '#f8fafc',
        p: { xs: 2, md: 3 },
        borderRadius: { xs: '0 0 12px 12px', md: '0 12px 12px 0' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <Typography
          sx={{
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: '0.05em',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            mb: 2
          }}
        >
          {t('calculator.selectedPlanIncludes', 'Selected Plan Includes')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {currentFeatures.map((feature) => (
            <Box
              key={feature.key}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
            >
              <CheckCircleIcon sx={{ color: '#00C853', fontSize: 22 }} />
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {t(`calculator.feature.${feature.key}`, feature.default)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* Compare Dialog */}
    <TierCompareDialog
      open={compareDialogOpen}
      onClose={() => setCompareDialogOpen(false)}
      region={region}
      selectedTier={packageTier}
      onTierSelect={(tier) => {
        setPackageTier(tier);
        setCompareDialogOpen(false);
      }}
      showAdvancedTier={showAdvancedTier}
      showPricesWithoutBonuses={showPricesWithoutBonuses}
    />
    </>
  );
};

export default React.memo(BusinessPhone);
